import { useState, useEffect, useMemo } from 'react';
import SearchModal from './SearchModal';
import { buildSearchPageUrl } from '@/features/search/utils/searchNavigation';
import { markSearchTriggered } from '@/features/search/utils/searchSession';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { trackSearchedForProduct } from '../../constants';

interface SearchKeyword {
  name: string;
  hexColor?: string;
}

interface SearchWidgetData {
  label?: string[];
  searchKeywords?: SearchKeyword[];
  showTitle?: boolean;
}

interface SearchWidgetProps {
  data?: SearchWidgetData;
  sectionKey?: string;
  eventLabel?: string;
  priority?: number;
}

// Default values if API doesn't provide them
const DEFAULT_LABEL = 'Search by';
const DEFAULT_KEYWORDS: SearchKeyword[] = [
  { name: 'Relationship', hexColor: '#D32F2F' },
  { name: 'Category', hexColor: '#D32F2F' },
  { name: 'Product', hexColor: '#D32F2F' },
];

export default function SearchWidget({ 
  data,
  sectionKey: _sectionKey, 
  eventLabel: _eventLabel, 
  priority: _priority 
}: SearchWidgetProps) {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [displayTextIndex, setDisplayTextIndex] = useState(0);
  const [animationPhase, setAnimationPhase] = useState<'visible' | 'exiting' | 'entering'>('visible');
  const { analytics, isInitialized } = useAnalytics();

  // Extract label and keywords from API data with fallback to defaults
  const label = data?.label?.[0] ?? DEFAULT_LABEL;
  const keywords = useMemo(() => {
    if (data?.searchKeywords && data.searchKeywords.length > 0) {
      return data.searchKeywords;
    }
    return DEFAULT_KEYWORDS;
  }, [data?.searchKeywords]);

  const searchTexts = useMemo(() => keywords.map(k => k.name), [keywords]);

  useEffect(() => {
    const interval = setInterval(() => {
      // Phase 1: Current text slides down (exiting)
      setAnimationPhase('exiting');
      
      // Phase 2: After slide down completes, change text and start slide up (entering)
      setTimeout(() => {
        // Use functional update to avoid dependency on displayTextIndex
        setDisplayTextIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % searchTexts.length;
          setAnimationPhase('entering');
          
          // Phase 3: After slide up completes, text is visible
          setTimeout(() => {
            setAnimationPhase('visible');
          }, 500); // Animation duration
          
          return nextIndex;
        });
      }, 500); // Wait for exit animation to complete
    }, 3000); // Change text every 3 seconds (2.5s visible + 0.5s animation)

    return () => clearInterval(interval);
  }, [searchTexts.length]);

  const handleSearch = (query: string) => {
    setIsSearchModalOpen(false);
    const trimmed = query.trim();
    if (!trimmed) return;
    trackSearchedForProduct(analytics, trimmed);
    markSearchTriggered();
    window.location.href = buildSearchPageUrl(trimmed);
  };

  const handleSearchInputClick = () => {
    if (analytics && isInitialized) {
      try {
        analytics.trackEvent('search_icon_click', {
          location: 'home_page_mobile',
          action: 'open_search_modal'
        });
      } catch (error) {
        console.error('Error tracking search input click:', error);
      }
    }
    setIsSearchModalOpen(true);
  };

  return (
    <>
      {/* Search Widget — sticky below mobile header so it stays visible on scroll */}
      <section className="sticky top-[54px] z-[49] bg-white shadow-sm pt-[8px] px-[10px] pb-[8px]">
          <div 
            onClick={handleSearchInputClick}
            className="w-full rounded-lg border border-[#D9C5C5] bg-white px-4 py-3 cursor-pointer transition-all hover:border-[#BFB9BD] text-left relative overflow-hidden"
          >
            <div className="text-sm text-gray-600 flex items-center">
              <span>{label} </span>
              <span className="relative inline-block ml-1 min-w-[100px] h-5 overflow-hidden">
                {/* Current text - visible or sliding down */}
                {animationPhase !== 'entering' && (
                  <span 
                    className={`absolute inset-0 font-medium transition-all duration-500 ease-in-out ${
                      animationPhase === 'exiting' 
                        ? 'translate-y-full opacity-0' 
                        : 'translate-y-0 opacity-100'
                    }`}
                    style={{ color: keywords[displayTextIndex]?.hexColor || '#D32F2F' }}
                  >
                    {searchTexts[displayTextIndex]}
                  </span>
                )}
                {/* Next text - sliding up from bottom */}
                {animationPhase === 'entering' && (
                  <span 
                    className="absolute inset-0 font-medium animate-slideUpFromBottom"
                    style={{ color: keywords[displayTextIndex]?.hexColor || '#D32F2F' }}
                  >
                    {searchTexts[displayTextIndex]}
                  </span>
                )}
              </span>
            </div>
          </div>
      </section>

      {/* Mobile Search Modal */}
      <SearchModal 
        isOpen={isSearchModalOpen} 
        onClose={() => setIsSearchModalOpen(false)}
        onSearch={handleSearch}
      />
    </>
  );
}

